import React from "react"
import * as Yup from "yup"
import { useFormik } from "formik"
import Ajax from "../../../Ajax"
import { Alert, AlertModal } from "../../.."
import Logo from "../../../misc/Logo"
import Field from "../../form/fields/Field"
import { Button, CircularProgress } from "@mui/material"
import { QuestionnaireSettings } from "../../../models/Interfaces"


interface FormValues {
    file: File | null;
}

interface FileWithMetadata extends File {
    type: string;
    size: number;
}

export default function QuestionnaireSettingsForm ({account}) {

    const [settings, setSettings] = React.useState<QuestionnaireSettings>(null)

    const getSettings = async () => {
        setSettings(null)

        await Ajax.Questionnaire.AdvisorGet(account.advisorUid).then((response) => {
            setSettings(response.data)
        }).catch(() => {
            Alert({message: "Failed to get settings"})
        })
    }

    const updateSettings = async (newSettings) => {
        await Ajax.Questionnaire.AdvisorUpdate(newSettings).then(() => {
            getSettings()
        }).catch(() => {
            Alert({message: "Failed to update settings"})
        })
    }

    React.useEffect(() => {
        getSettings()
    }, [])

    if (!settings) {
        return (
            <div style={{display: "flex", gap: "10px", alignItems: "center"}}>
                <CircularProgress style={{width: "14px", height: "14px", margin: "0"}} />
                <p>Loading Questionnaire settings...</p>
            </div>
        )
    }

    return (
        <div className="form">
            <SettingsForm
                settings={settings}
                updateSettings={(settings) => updateSettings(settings)} />
        </div>
    )
}


function SettingsForm ({settings, updateSettings}) {

    const formik = useFormik<QuestionnaireSettings>({

        initialValues: settings,

        validationSchema: Yup.object({
            displayName: Yup.string().required(),
            baseUri: Yup.string().when("embed", {
                is: (embed) => embed == true,
                then: () =>         Yup.string().matches(/(^(https|http)(:\/\/)([(?a-zA-Z0-9@:%._\-\+\/]{6,100})$)/, "Must be a valid URL").required("This field is required if the 'Embed' setting is on."),
                otherwise: () =>    Yup.string().matches(/(^(https|http)(:\/\/)([(?a-zA-Z0-9@:%._\-\+\/]{6,100})$)|(^$)/, "Must be a valid URL").nullable()
            }),
            embed: Yup.bool().required()
        }),

        onSubmit: async (values, actions) => {
            await updateSettings({...settings, ...values})
        }
    })

    return (
        <form className="form" onSubmit={formik.handleSubmit}>
            <h2>Questionnaire settings</h2>

            <div className="form-fields">
                {/* <Field.Text
                    formik
                    name="displayName"
                    label="Company display name"
                    helpText="This is the Company name which will be shown in your questionnaires."
                    warning={formik.submitCount && formik.errors.displayName ? formik.errors.displayName : false}
                    onChange={formik.handleChange}
                    value={formik.values.displayName} /> */}

                
                <Field.Switch
                    formik
                    name="embed"
                    label="Embed questionnaires" 
                    warning={false}
                    value={formik.values.embed}
                    onChange={formik.handleChange} />

                {formik.values.embed ? (
                    <>
                        <div>
                            <p>To embed your Questionnaires into a custom page on <b>your</b> website, you can paste the link to the desired page here.</p>
                            <p>Then paste <span className="link" onClick={() => AlertModal({body: <EmbedCode />, width: "600px"})}>this code</span> into the page on your web page.</p>
                        </div>

                        <Field.Text
                            formik
                            name="baseUri"
                            label="Embed URL"
                            placeholder="https://yourwebsite.com/questionnaire"
                            warning={formik.submitCount && formik.errors.baseUri ? formik.errors.baseUri : false}
                            onChange={formik.handleChange}
                            value={formik.values.baseUri} />
                    </>
                ) : (
                    <Field.Text
                        formik
                        name="baseUri"
                        label="Base URL"
                        placeholder="https://<subdomain>.aoralaw.com"
                        helpText="If not set, your base URL will be https://questionnaire.aoralaw.com"
                        warning={formik.submitCount && formik.errors.baseUri ? formik.errors.baseUri : false}
                        onChange={formik.handleChange}
                        value={formik.values.baseUri} />
                )} 

                <Button
                    className="btn btn-md"
                    variant="contained"
                    color="success"
                    type="submit"
                    disabled={(!formik.isValid && !!formik.submitCount) || formik.isSubmitting || !formik.dirty} >
                    Update settings
                </Button>
            </div>

            {!formik.values.embed ? (
                <LogoForm
                    settings={settings}
                    onSuccess={(logoUri) => {
                        updateSettings({...settings, logoUri: logoUri})
                    }} />
            ) : null}
        </form>
    )
}


function EmbedCode () {

    var code = '<div id="questionnaire-container"></div><script defer="defer" src="https://aora.lon1.cdn.digitaloceanspaces.com/js/aora-embed-questionnaire.js"></script>'

    return (
        <div className="form">
            <h2>Embed code</h2>

            <p>Insert this code into your web page in the place where you want the Questionnaire to appear.</p>

            <div style={{fontFamily: "monospace", backgroundColor: "#e9e9e9", wordBreak: "break-word", padding: "10px"}}>
                {code}
            </div>

            <Button
                onClick={() => navigator.clipboard.writeText(code)}
                className="btn btn-sm"
                variant="contained"
                color="success">
                Copy code
            </Button>
        </div>
    )
}


function LogoForm ({settings, onSuccess = (logoUri) => {}}) {

    const formik = useFormik<FormValues>({

        initialValues: {
            file: null
        },

        validationSchema: Yup.object({
            file: Yup.mixed<FileWithMetadata>()
                .required('A file is required')
                .test('fileFormat', 'Only PNG image files are allowed', value => {
                    return value && value.type === 'image/png';
                })
                .test('fileSize', 'File size must be less than 300kb', value => {
                    return value && value.size <= 300000; // 3MB in bytes
                }),
        }),

        onSubmit: async (values, actions) => {
            var formData = new FormData()

            if (formik.values.file) {
                formData.append("file", formik.values.file)
                var newFileName = `${settings.uid}-logo.png`

                await Ajax.Account.UploadPublicImage(formData, "advisor_images", newFileName).then((response) => {
                    onSuccess(response.data)
                }).catch((response) => {
                    Alert({message: response.data?.detail ? response.data.detail : "Failed to enable questionnaires"})
                })
            }
        }
    })

    return (
        <form style={{display: "grid", gap: "10px"}} onSubmit={formik.handleSubmit}>
            <div>
                <h3>Company logo</h3>
                <small>This logo is currently used to brand the header of your Questionnaires.</small>
            </div>

            {settings.logoUri ? (
                <img src={`${settings.logoUri}?${Math.random()}`} height="40px" alt="Not found" />
            ) : (
                <Logo height="40px" type="block" />
            )}

            <Field.File
                formik
                name="file"
                label="Choose a different logo"
                value={formik.values.file}
                onChange={(e) => formik.setFieldValue("file", e.target.value)}
                warning={formik.submitCount && formik.errors.file ? formik.errors.file : null} />

            <Button
                disabled={!formik.values.file || formik.isSubmitting}
                className="btn btn-md"
                variant="contained"
                color="success"
                onClick={() => formik.handleSubmit()}>
                Update logo
            </Button>
        </form>
    )
}